/** RFC 9396 `authorization_details` type for a Pryme mandate (collision-resistant URI under pryme.id). */
export const AUTHORIZATION_DETAILS_TYPE = 'https://pryme.id/authz/mandate/v1';
export function ed25519Jwk(publicKey, kid) {
    return kid ? { kty: 'OKP', crv: 'Ed25519', x: publicKey, kid } : { kty: 'OKP', crv: 'Ed25519', x: publicKey };
}
/** RFC 9396 `authorization_details` (an array, per the RFC) for a verified mandate payload. */
export function mandateAuthorizationDetails(m, opts = {}) {
    const c = m.constraints ?? {};
    const constraints = {};
    if (c.currency)
        constraints.currency = c.currency;
    if (c.maxPerTransaction)
        constraints.max_per_transaction = c.maxPerTransaction;
    if (c.maxTotal)
        constraints.max_total = c.maxTotal;
    if (c.allowedResources)
        constraints.allowed_resources = [...c.allowedResources];
    if (c.allowedMerchants)
        constraints.allowed_merchants = [...c.allowedMerchants];
    return [
        {
            type: AUTHORIZATION_DETAILS_TYPE,
            identifier: m.mandate_id,
            actions: [...m.permissions],
            ...(opts.locations?.length ? { locations: [...opts.locations] } : {}),
            requires_approval_for: [...m.requires_approval_for],
            constraints,
            agent: { id: m.agent_id, cnf: { jwk: ed25519Jwk(m.agent_key, m.agent_kid) } },
            effective_at: m.effective_at,
            expires_at: m.expires_at,
            version: m.version,
            online_check: true,
        },
    ];
}
/**
 * The AP2 error a verifier would report for a Pryme denial. `approval_required` maps to
 * `unresolved_constraint`, which AP2 lets an agent treat as "fall back to a directly approved
 * closed mandate": the same role as a Pryme approval. Codes that are not authorization
 * outcomes (rate limits, outages) have no AP2 equivalent.
 */
export const AP2_ERROR_FOR = {
    invalid_signature: 'invalid_credential',
    wrong_audience: 'invalid_credential',
    replay_detected: 'invalid_credential',
    expired_presentation: 'invalid_credential',
    unknown_pairwise_identity: 'invalid_credential',
    credential_revoked: 'invalid_credential',
    identity_not_verified: 'invalid_credential',
    assurance_insufficient: 'invalid_credential',
    mandate_revoked: 'invalid_mandate',
    mandate_expired: 'invalid_mandate',
    mandate_suspended: 'invalid_mandate',
    agent_not_authorized: 'invalid_mandate',
    constraint_violation: 'invalid_mandate',
    approval_denied: 'invalid_mandate',
    approval_required: 'unresolved_constraint',
};
